import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Input, MessageEvent, Output } from 'webmidi'

import { trackAnalyticsEvent } from '@/lib/analytics'
import {
  dx7BankVoiceCount,
  makeDx7BankPayload,
  makeDx7SingleVoicePayload,
  makeYamahaSysexMessage,
  unpackDx7Voice,
  type Dx7Voice,
} from '@/lib/dx7'
import { resolveFirmwareProfile, sendsBankTo } from '@/lib/firmware-profile'
import {
  fm1EffectParameterCount,
  fm1EffectParameterMaximums,
  normalizeFm1Effects,
} from '@/lib/fm1-effects'
import {
  classifyFm1Firmware,
  fm1IdentityQuery,
  mayBeSloop,
  parseFm1IdentityReply,
  type Fm1Firmware,
} from '@/lib/fm1-firmware'
import { reportBankTransferFailure } from '@/lib/monitoring'
import {
  formatMidiBytes,
  makeFm1EffectDiagnosticControlMessage,
  getMidiSupport,
  isHighRateMidiMessage,
  makeFm1EffectControlMessage,
  makeFm1ParameterPayload,
  makeFm1ProgramChangeMessage,
  makeLogEntry,
  portsToDevices,
  resolveMidiPortSelection,
  sendFm1Parameter,
  sendFm1ProgramChange,
  sendFm1EffectControl,
  sendFm1EffectDiagnosticControl,
  midiNoteCount,
  sendEveryNoteOff,
  sendNoteOff,
  sendNoteOn,
  sendDx7Bank,
  sendDx7Voice,
  type Fm1EffectProbeMode,
  type MidiDevice,
  type MidiLogEntry,
} from '@/lib/midi'
import { MidiTransferCancelledError, MidiTransferQueue } from '@/lib/midi-transfer-queue'
import { midiActivity } from '@/lib/midi-activity'
import { MidiLogStore } from '@/lib/midi-log-store'
import { defaultNoteVelocity } from '@/lib/note-velocity'
import type { OfficialOpenTrackDelivery } from '@/lib/official-open-track-send'

type WebMidiApi = (typeof import('webmidi'))['WebMidi']

export type StartNoteOptions = {
  /** Keeps the note out of the MIDI log, for notes a loop repeats several times a second. */
  quiet?: boolean
  velocity?: number
}

export type BankTransferResult =
  | { ok: true }
  | {
      ok: false
      reason: 'invalid_bank' | 'no_output' | 'sysex_unavailable' | 'transport' | 'unsupported'
    }

export type SharedEffectTransferResult =
  | { ok: true; sent: typeof fm1EffectParameterCount }
  | {
      ok: false
      reason: 'cancelled' | 'ineligible' | 'invalid' | 'no-output' | 'transport'
      sent: number
    }

export const midiChannels = Array.from({ length: 16 }, (_, index) => index + 1)

type MidiConnectionErrorCode =
  | 'disconnect_failed'
  | 'enable_failed'
  | 'insecure_context'
  | 'permission_denied'
  | 'unsupported_browser'

const midiStorageKeys = {
  autoConnect: 'fm1-midi-auto-connect',
  channel: 'fm1-midi-channel',
  effectChannel: 'fm1-midi-effect-channel',
  inputId: 'fm1-midi-input-id',
  outputId: 'fm1-midi-output-id',
} as const

const outputChangedMessage = 'Queued MIDI messages were dropped because the MIDI output changed.'

/**
 * The gap between the parameter changes that carry a whole patch. Live edits keep the queue's own
 * interval; a patch goes as fast as the FM1 takes it (docs/fm1-va-compatibility-tests.md, test 7c).
 */
const patchParameterIntervalMs = 0

/** How long to wait for the FM1 to answer the identity query, and how often to ask. */
const identityReplyTimeoutMs = 1000
const identityAttempts = 3

/** The firmware answer for one pair of ports, so an answer never outlives the ports it came from. */
type FirmwareIdentification = { firmware: Fm1Firmware; input: Input; output: Output }

/**
 * The last patch sent to an Official Open track, held while its ports stay selected. `announce`
 * says whether a sent patch is confirmed with a notification, as a slot click's is and an editor's
 * live edit's is not.
 */
type TrackDelivery = { announce: boolean; delivery: OfficialOpenTrackDelivery }

function firmwareLogMessage(firmware: Fm1Firmware) {
  switch (firmware.kind) {
    case 'mvave':
      return `The FM1 runs M-VAVE firmware ${firmware.identity}. Patches go to its edit buffer.`
    // FM-1+VA and the SMK-37 both store a dump; the SMK-37 is sent no bank either.
    case 'fm1-va':
    case 'smk37':
      return `Firmware ${firmware.identity} stores a single-patch dump over the selected preset, so patches are sent as parameter changes instead.`
    case 'official-open':
      return `The FM1 runs M-VAVE's Official Open firmware ${firmware.identity}, an eight-track groovebox. Patches go to the note channel's track once it says that track runs DX7, and no Program Change or effect controller is sent.`
    case 'felucca':
      return mayBeSloop(firmware)
        ? `The FM1 runs Felucca or SLOOP firmware ${firmware.identity}, a name both report, and either ignores DX7 patches, so the parameter changes sent to it have no effect.`
        : `The FM1 runs Felucca firmware ${firmware.identity}, which ignores DX7 patches, so the parameter changes sent to it have no effect.`
    default:
      return 'identity' in firmware && firmware.identity
        ? `The FM1 runs firmware ${firmware.identity}, which the editor does not recognise, so patches are sent as parameter changes.`
        : 'The FM1 did not say which firmware it runs, so patches are sent as parameter changes, which it does not store.'
  }
}

function readStoredValue(key: string) {
  try {
    return localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function storeValue(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // MIDI still works when storage is unavailable (for example, in private browsing).
  }
}

function readStoredChannel() {
  const storedChannel = Number(readStoredValue(midiStorageKeys.channel))
  return midiChannels.includes(storedChannel) ? storedChannel : 1
}

function readStoredEffectChannel() {
  const storedChannel = Number(readStoredValue(midiStorageKeys.effectChannel))
  return midiChannels.includes(storedChannel) ? storedChannel : 2
}

function midiConnectionFailureReason(error: unknown) {
  return error instanceof Error &&
    (error.name === 'NotAllowedError' || error.name === 'SecurityError')
    ? ('permission_denied' as const)
    : ('enable_failed' as const)
}

/** A failed send's log entry: the error's message, or `fallback` when it is not an `Error`. */
function failureEntry(caughtError: unknown, fallback: string) {
  return makeLogEntry('system', caughtError instanceof Error ? caughtError.message : fallback)
}

function noOutputEntry(action: string) {
  return makeLogEntry('system', `Could not ${action}; no MIDI output selected.`)
}

type LoggedLiveEdit = {
  /** Coalesces edits: a newer one with the same key replaces one still waiting in the queue. */
  key: string
  /** Builds the bytes to log, validating the edit before it is queued. */
  makeMessage: () => Uint8Array
  send: () => void
  sent: string
  /** Logged when the queued send fails or is dropped. */
  failure: string
  /** Logged when the message cannot be built; `failure` unless given. */
  buildFailure?: string
}

export function useMidi() {
  const [midiAccess, setMidiAccess] = useState(false)
  const [sysexEnabled, setSysexEnabled] = useState(false)
  const [outputs, setOutputs] = useState<MidiDevice<Output>[]>([])
  const [inputs, setInputs] = useState<MidiDevice<Input>[]>([])
  const [selectedOutputId, setSelectedOutputId] = useState('')
  const [selectedInputId, setSelectedInputId] = useState('')
  const [channel, setChannelState] = useState(readStoredChannel)
  const [effectChannel, setEffectChannelState] = useState(readStoredEffectChannel)
  // MIDI left connected connects by itself as the page starts, so it says so from the first render
  // and the header holds the room the connection and the FM1's badge will fill.
  const [isConnecting, setIsConnecting] = useState(
    () =>
      getMidiSupport() === 'supported' && readStoredValue(midiStorageKeys.autoConnect) === 'true',
  )
  const [error, setError] = useState<MidiConnectionErrorCode | null>(null)
  const [identification, setIdentification] = useState<FirmwareIdentification | null>(null)
  // The firmware families already reported to analytics. Each is reported once per page load, so
  // the split between them counts sessions rather than how often an FM1 was plugged back in.
  const reportedFirmwareKinds = useRef(new Set<Fm1Firmware['kind']>())
  // Counts MIDI panics, so a player can stop rather than strike the released notes again.
  const [midiPanicCount, setMidiPanicCount] = useState(0)
  const [logStore] = useState(
    () => new MidiLogStore([makeLogEntry('system', 'Ready. Connect MIDI to begin.')]),
  )
  const [transferQueue] = useState(() => new MidiTransferQueue({ minimumIntervalMs: 35 }))
  const nextEffectOrder = useRef(0)
  const queueEffectOrder = useCallback(
    async (run: () => Promise<void>, signal: AbortSignal) => {
      const group = `effect-order-${++nextEffectOrder.current}`
      const cancel = () =>
        transferQueue.clearGroup(group, 'The effect order operation was cancelled.')
      signal.addEventListener('abort', cancel, { once: true })
      try {
        if (signal.aborted) return
        await transferQueue.enqueue(run, undefined, { group })
      } finally {
        signal.removeEventListener('abort', cancel)
      }
    },
    [transferQueue],
  )
  const sharedEffectBatches = useRef(new Set<string>())
  const nextSharedEffectBatch = useRef(0)
  const webMidi = useRef<WebMidiApi | null>(null)
  const webMidiLoader = useRef<Promise<WebMidiApi> | null>(null)
  const preferredOutputId = useRef(readStoredValue(midiStorageKeys.outputId))
  const preferredInputId = useRef(readStoredValue(midiStorageKeys.inputId))
  const startupConnectionAttempted = useRef(false)
  const [trackDelivery, setTrackDelivery] = useState<TrackDelivery | null>(null)
  // Aborted when the ports change or MIDI is switched off, so a read of Official Open's tracks
  // belongs to the ports it started on.
  const trackReads = useRef(new AbortController())

  const midiSupport = getMidiSupport()

  const selectedOutput = useMemo(
    () => outputs.find((device) => device.id === selectedOutputId)?.port,
    [outputs, selectedOutputId],
  )

  const selectedInput = useMemo(
    () => inputs.find((device) => device.id === selectedInputId)?.port,
    [inputs, selectedInputId],
  )

  const hasMidiOutput = Boolean(selectedOutput)
  const hasMidiInput = Boolean(selectedInput)
  const sysexAvailable = midiAccess && sysexEnabled

  // Asking needs SysEx and both of the FM1's ports. Until the answer for the ports in use arrives,
  // the firmware is being checked, so nothing is sent as though it were known.
  const canIdentify = sysexAvailable && Boolean(selectedOutput) && Boolean(selectedInput)
  const firmware = useMemo<Fm1Firmware>(() => {
    if (!canIdentify) return { kind: 'unidentified' }
    const answered =
      identification !== null &&
      identification.output === selectedOutput &&
      identification.input === selectedInput
    return answered ? identification.firmware : { kind: 'checking' }
  }, [canIdentify, identification, selectedInput, selectedOutput])

  const appendLog = useCallback(
    (entry: MidiLogEntry) => {
      logStore.append(entry)
    },
    [logStore],
  )

  /**
   * Queues one live edit and logs it with its bytes once sent. Returns whether it was queued; one
   * that cannot be built is logged and not queued.
   */
  const enqueueLogged = useCallback(
    ({ key, makeMessage, send, sent, failure, buildFailure = failure }: LoggedLiveEdit) => {
      try {
        const message = makeMessage()
        void transferQueue
          .enqueue(() => {
            send()
            appendLog(makeLogEntry('out', sent, message))
          }, key)
          .catch((caughtError) => appendLog(failureEntry(caughtError, failure)))
        return true
      } catch (caughtError) {
        appendLog(failureEntry(caughtError, buildFailure))
        return false
      }
    },
    [appendLog, transferQueue],
  )

  const loadWebMidi = useCallback(() => {
    if (!webMidiLoader.current) {
      webMidiLoader.current = import('webmidi')
        .then((module) => {
          webMidi.current = module.WebMidi
          return module.WebMidi
        })
        .catch((loadError) => {
          webMidiLoader.current = null
          throw loadError
        })
    }
    return webMidiLoader.current
  }, [])

  const refreshDevices = useCallback(
    (reason: 'changed' | 'connected') => {
      const activeWebMidi = webMidi.current
      if (!activeWebMidi) return

      const nextOutputs = portsToDevices(activeWebMidi.outputs)
      const nextInputs = portsToDevices(activeWebMidi.inputs)
      const chosenOutputId = preferredOutputId.current
      const outputId = resolveMidiPortSelection(nextOutputs, chosenOutputId, reason)
      const inputId = resolveMidiPortSelection(nextInputs, preferredInputId.current, reason)
      // The port in use is the one to wait for if it disconnects. A port missing when MIDI connects
      // is forgotten for this session, so the device picked in its place is kept from then on.
      if (reason === 'connected' || outputId) preferredOutputId.current = outputId
      if (reason === 'connected' || inputId) preferredInputId.current = inputId

      // An answer holds only while its ports stay selected. WebMidi hands back the same port object
      // when a device returns, and an FM1 may come back with other firmware, so it is asked again.
      const output = nextOutputs.find((device) => device.id === outputId)?.port
      const input = nextInputs.find((device) => device.id === inputId)?.port
      setIdentification((current) =>
        current && current.output === output && current.input === input ? current : null,
      )
      setOutputs(nextOutputs)
      setInputs(nextInputs)
      setSelectedOutputId(outputId)
      setSelectedInputId(inputId)
      if (reason === 'changed' && chosenOutputId && !outputId) {
        appendLog(
          makeLogEntry(
            'system',
            'The selected MIDI output disconnected. Reconnect it or choose another output.',
          ),
        )
      }
    },
    [appendLog],
  )

  const enableMidi = useCallback(
    async (remember: boolean) => {
      const method = remember ? ('manual' as const) : ('automatic' as const)
      if (midiSupport !== 'supported') {
        const reason = midiSupport === 'insecure' ? 'insecure_context' : 'unsupported_browser'
        setError(reason)
        trackAnalyticsEvent({ data: { method, reason }, name: 'midi_connection_failed' })
        return
      }

      setIsConnecting(true)
      setError(null)

      try {
        const activeWebMidi = await loadWebMidi()
        await activeWebMidi.enable({ sysex: true })
        setMidiAccess(true)
        setSysexEnabled(Boolean(activeWebMidi.sysexEnabled))
        if (remember) storeValue(midiStorageKeys.autoConnect, 'true')
        refreshDevices('connected')
        appendLog(
          makeLogEntry(
            'system',
            `MIDI connected${activeWebMidi.sysexEnabled ? ' with SysEx enabled' : ''}.`,
          ),
        )
      } catch (caughtError) {
        const reason = midiConnectionFailureReason(caughtError)
        setError(reason)
        trackAnalyticsEvent({
          data: { method, reason },
          name: 'midi_connection_failed',
        })
      } finally {
        setIsConnecting(false)
      }
    },
    [appendLog, loadWebMidi, midiSupport, refreshDevices],
  )

  const connectMidi = useCallback(() => enableMidi(true), [enableMidi])

  const disconnectMidi = useCallback(async () => {
    transferQueue.clear('Queued MIDI messages were dropped because MIDI was switched off.')
    setIsConnecting(true)
    setError(null)

    try {
      await webMidi.current?.disable()
      storeValue(midiStorageKeys.autoConnect, 'false')
      setMidiAccess(false)
      setSysexEnabled(false)
      setOutputs([])
      setInputs([])
      setSelectedOutputId('')
      setSelectedInputId('')
      setIdentification(null)
      appendLog(makeLogEntry('system', 'MIDI disconnected.'))
    } catch {
      setError('disconnect_failed')
    } finally {
      setIsConnecting(false)
    }
  }, [appendLog, transferQueue])

  useEffect(() => {
    if (
      startupConnectionAttempted.current ||
      readStoredValue(midiStorageKeys.autoConnect) !== 'true'
    ) {
      return
    }

    startupConnectionAttempted.current = true
    void enableMidi(false)
  }, [enableMidi])

  useEffect(() => {
    // Each queued message was meant for the output selected when it was queued. Drop the rest when
    // that output changes or disconnects, or on teardown, so they never reach another device or a
    // closed port. The queue stays usable after `clear`, so a Strict Mode remount keeps working.
    return () => transferQueue.clear(outputChangedMessage)
  }, [selectedOutput, transferQueue])

  useEffect(() => {
    // A full shared-effects recall belongs to the output, Effect channel, and identified firmware
    // it started with. A change drops only its unsent controls, never redirects them.
    const batches = sharedEffectBatches.current
    return () => {
      batches.forEach((group) => transferQueue.clearGroup(group, 'The shared FX route changed.'))
    }
  }, [effectChannel, firmware, selectedOutput, transferQueue])

  useEffect(() => {
    const reads = new AbortController()
    trackReads.current = reads
    return () => {
      reads.abort()
      setTrackDelivery(null)
    }
  }, [selectedInput, selectedOutput])

  // An input of the same name as the chosen output is the same device, so the firmware answer
  // comes from the device patches go to, not another one plugged in beside it.
  const selectOutput = useCallback(
    (id: string) => {
      preferredOutputId.current = id
      storeValue(midiStorageKeys.outputId, id)
      setSelectedOutputId(id)
      const name = outputs.find((device) => device.id === id)?.name
      const input = inputs.find((device) => device.name === name)
      if (input) {
        preferredInputId.current = input.id
        storeValue(midiStorageKeys.inputId, input.id)
        setSelectedInputId(input.id)
      }
    },
    [inputs, outputs],
  )

  const selectInput = useCallback((id: string) => {
    preferredInputId.current = id
    storeValue(midiStorageKeys.inputId, id)
    setSelectedInputId(id)
  }, [])

  const setChannel = useCallback((nextChannel: number) => {
    if (!midiChannels.includes(nextChannel)) return
    setChannelState(nextChannel)
    storeValue(midiStorageKeys.channel, String(nextChannel))
  }, [])

  const setEffectChannel = useCallback((nextChannel: number) => {
    if (!midiChannels.includes(nextChannel)) return
    setEffectChannelState(nextChannel)
    storeValue(midiStorageKeys.effectChannel, String(nextChannel))
  }, [])

  useEffect(() => {
    if (!midiAccess) {
      return
    }

    const listener = webMidi.current?.addListener('portschanged', () => {
      refreshDevices('changed')
      appendLog(makeLogEntry('system', 'MIDI device list changed.'))
    })

    return () => listener?.remove()
  }, [appendLog, midiAccess, refreshDevices])

  const sendBank = useCallback(
    (bank: string, voices: Dx7Voice[]) => {
      if (!selectedOutput) {
        appendLog(noOutputEntry(`send bank ${bank}`))
        return Promise.resolve<BankTransferResult>({ ok: false, reason: 'no_output' })
      }

      if (!webMidi.current?.sysexEnabled) {
        appendLog(makeLogEntry('system', 'Enable SysEx before connecting MIDI to send a bank.'))
        return Promise.resolve<BankTransferResult>({ ok: false, reason: 'sysex_unavailable' })
      }

      if (voices.length !== dx7BankVoiceCount) {
        appendLog(
          makeLogEntry('system', `Bank ${bank} is not loaded with ${dx7BankVoiceCount} voices.`),
        )
        return Promise.resolve<BankTransferResult>({ ok: false, reason: 'invalid_bank' })
      }

      // The SMK-37 saves a dump over its stored presets and cannot back them up; its log says so.
      if (!sendsBankTo(firmware, selectedOutput.name)) {
        return Promise.resolve<BankTransferResult>({ ok: false, reason: 'unsupported' })
      }

      const payload = makeDx7BankPayload(voices, channel)
      const message = makeYamahaSysexMessage(payload)

      appendLog(
        makeLogEntry('out', `Sending DX7 bank ${bank} (${dx7BankVoiceCount} voices)…`, message),
      )
      return transferQueue
        .enqueue(() => sendDx7Bank(selectedOutput, channel, voices))
        .then(() => {
          // The "Sending" entry already holds the full message, so this one does not repeat it.
          appendLog(makeLogEntry('out', `Sent bank ${bank}. Choose its destination on the FM1.`))
          return { ok: true } as const
        })
        .catch((caughtError) => {
          if (caughtError instanceof MidiTransferCancelledError) {
            // Switching MIDI off or changing output dropped the bank before it was sent. That is
            // not a transport failure, so it stays out of monitoring.
            appendLog(makeLogEntry('system', `Bank ${bank} was not sent. ${caughtError.message}`))
            return { ok: false, reason: 'no_output' } as const
          }
          reportBankTransferFailure({
            channel,
            stage: 'controller',
            sysexAvailable: Boolean(webMidi.current?.sysexEnabled),
            voiceCount: voices.length,
          })
          appendLog(failureEntry(caughtError, 'Bank transfer failed.'))
          return { ok: false, reason: 'transport' } as const
        })
    },
    [appendLog, channel, firmware, selectedOutput, transferQueue],
  )

  /**
   * Sends a patch as one DX7 parameter change for each of its 155 voice parameters. FM-1+VA holds
   * these as an unsaved edit of the selected preset, where it stores a single-voice dump at once.
   * Each shares its key with the live edit of the same parameter, so a newer patch or edit replaces
   * a value still waiting to be sent.
   */
  const sendVoiceAsParameters = useCallback(
    (output: Output, voice: Dx7Voice) => {
      let values: Uint8Array
      try {
        values = unpackDx7Voice(voice)
        values.forEach((value, parameter) => makeFm1ParameterPayload(parameter, value))
      } catch (caughtError) {
        appendLog(failureEntry(caughtError, 'Patch transfer failed.'))
        return Promise.resolve(false)
      }

      appendLog(makeLogEntry('out', `Sending ${voice.name} as ${values.length} parameter changes…`))
      const transfers = Array.from(values, (value, parameter) =>
        transferQueue.enqueue(
          () => sendFm1Parameter(output, parameter, value),
          `parameter-${parameter}`,
          { minimumIntervalMs: patchParameterIntervalMs },
        ),
      )
      return Promise.all(transfers)
        .then(() => {
          appendLog(
            makeLogEntry(
              'out',
              `Sent ${voice.name} as parameter changes, an unsaved edit of the selected preset. Save on the FM1 to store it.`,
            ),
          )
          return true
        })
        .catch((caughtError) => {
          if (caughtError instanceof MidiTransferCancelledError) {
            appendLog(makeLogEntry('system', `${voice.name} was not sent. ${caughtError.message}`))
            return false
          }
          appendLog(failureEntry(caughtError, 'Patch transfer failed.'))
          return false
        })
    },
    [appendLog, transferQueue],
  )

  /**
   * Sends a patch to the FM1: to the edit buffer, or on Official Open to the track on the note
   * channel. `announce` false keeps a patch sent to a track from being confirmed in a notification,
   * as each live edit in the editor is.
   */
  const sendVoice = useCallback(
    (voice: Dx7Voice, announce = true) => {
      if (!selectedOutput) {
        appendLog(noOutputEntry(`send ${voice.name}`))
        return Promise.resolve(false)
      }
      if (!webMidi.current?.sysexEnabled) {
        appendLog(makeLogEntry('system', 'Enable SysEx before sending a browser patch.'))
        return Promise.resolve(false)
      }

      const { presetEffects, singleVoiceDumps, trackEngineRead } =
        resolveFirmwareProfile(firmware).capabilities
      if (trackEngineRead) {
        const signal = trackReads.current.signal
        return import('@/lib/official-open-track-send')
          .then((module) =>
            module.sendVoiceToOfficialOpenTrack({
              channel,
              input: selectedInput,
              log: (direction, message, data) => appendLog(makeLogEntry(direction, message, data)),
              output: selectedOutput,
              queue: transferQueue,
              sendDump: () => sendDx7Voice(selectedOutput, channel, voice),
              signal,
              voice,
            }),
          )
          .then((delivery) => {
            if (!signal.aborted) setTrackDelivery({ announce, delivery })
            return delivery.type === 'sent'
          })
          .catch((caughtError) => {
            appendLog(failureEntry(caughtError, 'Patch transfer failed.'))
            return false
          })
      }
      if (!singleVoiceDumps) return sendVoiceAsParameters(selectedOutput, voice)

      const payload = makeDx7SingleVoicePayload(voice, channel)
      const message = makeYamahaSysexMessage(payload)
      appendLog(makeLogEntry('out', `Sending ${voice.name} to the FM1 edit buffer…`, message))
      // Only M-VAVE's firmware reaches here. V16, which moved the effects out of the preset, offers
      // Preset in its SAVE menu only when SAVE opens from a voice editing page
      // (docs/hardware-runs/stock-v16-2026-10-23.md, step 5).
      const storeHint = presetEffects
        ? 'Hold SAVE on the FM1 to store it.'
        : 'To store it, open SAVE on the FM1 from a voice editing page, such as OSC, and choose Preset.'

      return transferQueue
        .enqueue(() => sendDx7Voice(selectedOutput, channel, voice))
        .then(() => {
          appendLog(makeLogEntry('out', `Sent ${voice.name}. ${storeHint}`, message))
          return true
        })
        .catch((caughtError) => {
          if (caughtError instanceof MidiTransferCancelledError) {
            // Switching MIDI off or changing output dropped the patch before it was sent.
            appendLog(makeLogEntry('system', `${voice.name} was not sent. ${caughtError.message}`))
            return false
          }
          appendLog(failureEntry(caughtError, 'Patch transfer failed.'))
          return false
        })
    },
    [
      appendLog,
      channel,
      firmware,
      selectedInput,
      selectedOutput,
      sendVoiceAsParameters,
      transferQueue,
    ],
  )

  const sendProgramChange = useCallback(
    (program: number) => {
      if (!selectedOutput) {
        appendLog(noOutputEntry('select an FM1 program'))
        return false
      }

      try {
        const message = makeFm1ProgramChangeMessage(program, channel)
        sendFm1ProgramChange(selectedOutput, channel, program)
        appendLog(
          makeLogEntry('out', `Selected FM1 program ${program} on channel ${channel}.`, message),
        )
        return true
      } catch (caughtError) {
        appendLog(failureEntry(caughtError, 'FM1 program selection failed.'))
        return false
      }
    },
    [appendLog, channel, selectedOutput],
  )

  const sendParameter = useCallback(
    (parameter: number, value: number) => {
      if (!selectedOutput) {
        appendLog(noOutputEntry('send FM1 parameter'))
        return false
      }
      if (!webMidi.current?.sysexEnabled) {
        appendLog(makeLogEntry('system', 'Enable SysEx before testing parameter editing.'))
        return false
      }

      return enqueueLogged({
        key: `parameter-${parameter}`,
        makeMessage: () => makeYamahaSysexMessage(makeFm1ParameterPayload(parameter, value)),
        send: () => sendFm1Parameter(selectedOutput, parameter, value),
        sent: `Sent FM1 parameter ${parameter} = ${value}.`,
        failure: 'FM1 parameter write failed.',
        buildFailure: 'FM1 parameter test failed.',
      })
    },
    [appendLog, enqueueLogged, selectedOutput],
  )

  const sendEffectParameter = useCallback(
    (controller: number, value: number) => {
      if (!selectedOutput) {
        appendLog(noOutputEntry('send FM1 effect'))
        return false
      }
      const { capabilities, effectControllerBase: base } = resolveFirmwareProfile(firmware)
      if (!capabilities.effectControllers) return false

      return enqueueLogged({
        key: `effect-${controller}`,
        makeMessage: () => makeFm1EffectControlMessage(controller, value, effectChannel, base),
        send: () => sendFm1EffectControl(selectedOutput, effectChannel, controller, value, base),
        sent: `Sent FM1 effect CC ${base + controller} = ${value} on channel ${effectChannel}.`,
        failure: 'FM1 effect write failed.',
      })
    },
    [appendLog, effectChannel, enqueueLogged, firmware, selectedOutput],
  )

  const sendSharedEffectParameter = useCallback(
    (controller: number, value: number) => {
      if (!resolveFirmwareProfile(firmware).capabilities.sharedEffects) return false
      return sendEffectParameter(controller, value)
    },
    [firmware, sendEffectParameter],
  )

  const sendEffectDiagnosticControl = useCallback(
    (controller: number, value: number, mode: Fm1EffectProbeMode = 'profile') => {
      if (!import.meta.env.DEV) return false

      if (!selectedOutput) {
        appendLog(noOutputEntry('send development FX probe'))
        return false
      }
      // V16 research sends whatever the FM1 runs, for the V16 hardware run; otherwise only the
      // controllers the profile places the effects at.
      const { capabilities, effectControllerBase: base } = resolveFirmwareProfile(firmware)
      const offered =
        capabilities.effectControllers &&
        controller >= base &&
        controller < base + fm1EffectParameterCount
      if (mode === 'profile' && !offered) {
        appendLog(makeLogEntry('system', 'This firmware takes no FM1 effect controllers.'))
        return false
      }

      return enqueueLogged({
        key: `effect-diagnostic-${controller}`,
        makeMessage: () => makeFm1EffectDiagnosticControlMessage(controller, value, effectChannel),
        send: () =>
          sendFm1EffectDiagnosticControl(selectedOutput, effectChannel, controller, value),
        sent: `Sent ${mode === 'v16-research' ? 'V16 research' : 'development'} FX probe CC ${controller} = ${value} on channel ${effectChannel}.`,
        failure: 'Development FX probe failed.',
      })
    },
    [appendLog, effectChannel, enqueueLogged, firmware, selectedOutput],
  )

  const sendEffectSettings = useCallback(
    (settings: Uint8Array) => {
      const normalized = normalizeFm1Effects(settings)
      if (!selectedOutput) {
        appendLog(noOutputEntry('send FM1 effects'))
        return Promise.resolve(false)
      }
      // Official Open has no FX Channel effect unit and takes these as the track's volume and pan,
      // so there is nothing to send, and a patch it is sent with has still gone as far as it can.
      const { capabilities, effectControllerBase: base } = resolveFirmwareProfile(firmware)
      if (!capabilities.effectControllers) {
        appendLog(makeLogEntry('system', 'This firmware takes no FM1 effect controllers.'))
        return Promise.resolve(true)
      }

      const transfers = Array.from({ length: fm1EffectParameterCount }, (_, controller) =>
        transferQueue.enqueue(
          () =>
            sendFm1EffectControl(
              selectedOutput,
              effectChannel,
              controller,
              normalized[controller],
              base,
            ),
          `effect-${controller}`,
        ),
      )

      appendLog(makeLogEntry('out', `Sending FM1 effect unit on channel ${effectChannel}…`))
      return Promise.all(transfers)
        .then(() => true)
        .catch((caughtError) => {
          appendLog(failureEntry(caughtError, 'FM1 effect transfer failed.'))
          return false
        })
    },
    [appendLog, effectChannel, firmware, selectedOutput, transferQueue],
  )

  const sendSharedEffectSettings = useCallback(
    async (settings: Uint8Array, signal?: AbortSignal): Promise<SharedEffectTransferResult> => {
      if (
        !(settings instanceof Uint8Array) ||
        settings.length !== fm1EffectParameterCount ||
        settings.some((value, controller) => value > fm1EffectParameterMaximums[controller])
      ) {
        appendLog(makeLogEntry('system', 'The shared FX preset is invalid and was not sent.'))
        return { ok: false, reason: 'invalid', sent: 0 }
      }
      if (!selectedOutput) {
        appendLog(noOutputEntry('send shared FM1 effects'))
        return { ok: false, reason: 'no-output', sent: 0 }
      }
      const profile = resolveFirmwareProfile(firmware)
      if (!profile.capabilities.sharedEffects) {
        return { ok: false, reason: 'ineligible', sent: 0 }
      }

      const captured = settings.slice()
      const group = `shared-effects-${++nextSharedEffectBatch.current}`
      sharedEffectBatches.current.add(group)
      // The caller leaving drops the controls still waiting their turn, as a route change does.
      const cancel = () => transferQueue.clearGroup(group, 'The shared FX transfer was cancelled.')
      let sent = 0
      let transportError: unknown
      const transfers = Array.from(captured, (value, controller) =>
        transferQueue.enqueue(
          () => {
            try {
              sendFm1EffectControl(
                selectedOutput,
                effectChannel,
                controller,
                value,
                profile.effectControllerBase,
              )
              sent += 1
            } catch (caughtError) {
              transportError = caughtError
              transferQueue.clearGroup(group, 'The shared FX transfer stopped after a failure.')
              throw caughtError
            }
          },
          undefined,
          { group },
        ),
      )
      appendLog(makeLogEntry('out', `Sending shared FM1 effects on channel ${effectChannel}…`))
      if (signal?.aborted) cancel()
      else signal?.addEventListener('abort', cancel, { once: true })
      try {
        await Promise.all(transfers)
        appendLog(makeLogEntry('out', 'Sent the shared FM1 effects.'))
        return { ok: true, sent: fm1EffectParameterCount }
      } catch (caughtError) {
        const cancelled =
          transportError === undefined && caughtError instanceof MidiTransferCancelledError
        transferQueue.clearGroup(
          group,
          cancelled ? caughtError.message : 'The shared FX transfer stopped after a failure.',
        )
        await Promise.allSettled(transfers)
        appendLog(
          cancelled
            ? makeLogEntry(
                'system',
                `The shared FM1 effects were not all sent. ${caughtError.message}`,
              )
            : failureEntry(transportError ?? caughtError, 'Shared FM1 effect transfer failed.'),
        )
        return { ok: false, reason: cancelled ? 'cancelled' : 'transport', sent }
      } finally {
        signal?.removeEventListener('abort', cancel)
        sharedEffectBatches.current.delete(group)
      }
    },
    [appendLog, effectChannel, firmware, selectedOutput, transferQueue],
  )

  const startNote = useCallback(
    (note: number, label: string, options?: StartNoteOptions) => {
      const velocity = options?.velocity ?? defaultNoteVelocity

      if (!selectedOutput) {
        if (!options?.quiet) {
          appendLog(makeLogEntry('system', `Pressed ${label}; no output yet.`))
        }
        return
      }

      try {
        sendNoteOn(selectedOutput, channel, note, velocity)
        // A looping phrase sends notes several times a second, which would bury the log it
        // shares with everything else the editor sends, so it logs itself starting instead.
        if (!options?.quiet) {
          appendLog(
            makeLogEntry('out', `Ch ${channel} Note On: ${label}`, [
              0x90 | ((channel - 1) & 0x0f),
              note,
              velocity,
            ]),
          )
        }
      } catch (caughtError) {
        appendLog(failureEntry(caughtError, 'MIDI note-on failed.'))
      }
    },
    [appendLog, channel, selectedOutput],
  )

  /**
   * A looping audition phrase sends its notes quietly, so the log says when a phrase starts and
   * stops instead of filling with the notes between.
   */
  const logAuditionPhrase = useCallback(
    (phraseId: string, state: 'started' | 'stopped') => {
      appendLog(
        makeLogEntry('system', `Audition phrase “${phraseId}” ${state} on channel ${channel}.`),
      )
    },
    [appendLog, channel],
  )

  const stopNote = useCallback(
    (note: number) => {
      if (selectedOutput) {
        try {
          sendNoteOff(selectedOutput, channel, note)
        } catch (caughtError) {
          appendLog(failureEntry(caughtError, 'MIDI note-off failed.'))
        }
      }
    },
    [appendLog, channel, selectedOutput],
  )

  /** A MIDI panic: releases every note on the note channel, for notes left hanging on the FM1. */
  const sendMidiPanic = useCallback(() => {
    if (!selectedOutput) {
      appendLog(noOutputEntry('send a MIDI panic'))
      return false
    }

    try {
      sendEveryNoteOff(selectedOutput, channel)
    } catch (caughtError) {
      appendLog(failureEntry(caughtError, 'MIDI panic failed.'))
      return false
    }

    appendLog(
      makeLogEntry(
        'out',
        `MIDI panic: sent Note Off for all ${midiNoteCount} notes on channel ${channel}.`,
      ),
    )
    setMidiPanicCount((count) => count + 1)
    return true
  }, [appendLog, channel, selectedOutput])

  // Asks the FM1 which firmware it runs whenever the ports in use change, as a reconnected FM1 may
  // have been updated while it was away. The query is the updater's read-only identity question.
  useEffect(() => {
    if (!canIdentify || !selectedOutput || !selectedInput) return

    const output = selectedOutput
    const input = selectedInput
    let attempts = 0
    let timer: number | undefined
    let finished = false

    const finish = (answer: Fm1Firmware) => {
      finished = true
      window.clearTimeout(timer)
      input.removeListener('midimessage', hear)
      setIdentification({ firmware: answer, input, output })
      appendLog(makeLogEntry('system', firmwareLogMessage(answer)))
      if (answer.kind !== 'checking' && !reportedFirmwareKinds.current.has(answer.kind)) {
        reportedFirmwareKinds.current.add(answer.kind)
        trackAnalyticsEvent({ data: { firmware: answer.kind }, name: 'fm1_identified' })
      }
    }
    function hear(event: MessageEvent) {
      const identity = parseFm1IdentityReply(event.data)
      if (identity) finish(classifyFm1Firmware(identity))
    }
    const ask = () => {
      if (attempts === identityAttempts) {
        finish({ kind: 'unidentified' })
        return
      }
      attempts += 1
      try {
        output.send(fm1IdentityQuery)
        midiActivity.signal('out')
        appendLog(makeLogEntry('out', 'Asked the FM1 which firmware it runs.', fm1IdentityQuery))
      } catch {
        finish({ kind: 'unidentified' })
        return
      }
      timer = window.setTimeout(ask, identityReplyTimeoutMs)
    }

    input.addListener('midimessage', hear)
    ask()

    return () => {
      // An answer holds only while its ports stay selected, so choosing another port and then this
      // one again asks again rather than bringing back the answer from before.
      setIdentification(null)
      if (finished) return
      window.clearTimeout(timer)
      input.removeListener('midimessage', hear)
    }
  }, [appendLog, canIdentify, selectedInput, selectedOutput])

  useEffect(() => {
    if (!selectedInput) {
      return
    }

    const handleMidiMessage = (event: MessageEvent) => {
      if (isHighRateMidiMessage(event.data)) return
      midiActivity.signal('in')
      appendLog(makeLogEntry('in', formatMidiBytes(event.data), event.data))
    }

    selectedInput.addListener('midimessage', handleMidiMessage)

    return () => selectedInput.removeListener('midimessage', handleMidiMessage)
  }, [appendLog, selectedInput])

  return {
    midiPanicCount,
    channel,
    connectMidi,
    disconnectMidi,
    error,
    effectChannel,
    firmware,
    hasMidiOutput,
    hasMidiInput,
    inputs,
    isConnecting,
    logAuditionPhrase,
    logStore,
    midiAccess,
    outputs,
    queueEffectOrder,
    sendBank,
    sendEffectDiagnosticControl,
    sendEffectParameter,
    sendEffectSettings,
    sendSharedEffectParameter,
    sendSharedEffectSettings,
    sendParameter,
    sendProgramChange,
    sendVoice,
    selectedInputId,
    selectedOutputId,
    setChannel,
    setEffectChannel,
    setSelectedInputId: selectInput,
    setSelectedOutputId: selectOutput,
    sendMidiPanic,
    startNote,
    stopNote,
    sysexAvailable,
    trackDelivery,
  }
}

export type MidiController = ReturnType<typeof useMidi>
